import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, Empty, Form } from 'antd';
import React, { useContext } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { DisclaimerKind } from 'app/typings/disclaimers';

import { DisclaimerBloc } from './DisclaimerBloc';

const DISCLAIMER_KIND_LABELS: Record<DisclaimerKind, string> = {
  [DisclaimerKind.ADVICE]: 'Advice (Cool to know)',
  [DisclaimerKind.ALERT]: 'Alert (Attention please)',
  [DisclaimerKind.IMPORTANT_INFORMATION]: 'Important information',
};

type Props = {
  kind: DisclaimerKind;
  filterByKind?: boolean;
  packageId?: number;
};

export function DisclaimerContent({
  kind,
  filterByKind = true,
  packageId,
}: Props) {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const allDisclaimers = Form.useWatch('disclaimers', form) || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <div className="disclaimer-category">
      <SCard title={DISCLAIMER_KIND_LABELS[kind]}>
        <Form.List name="disclaimers">
          {(fields, { add, remove, move }) => {
            const filteredFields = fields.filter((field) => {
              if (!filterByKind) {
                return true;
              }

              const disclaimer = allDisclaimers[field.name];

              return disclaimer?.kind === kind;
            });

            const handleDragEnd = ({ active, over }: DragEndEvent) => {
              if (!over || active.id === over.id) {
                return;
              }

              const from = filteredFields.findIndex((f) => f.key === active.id);
              const to = filteredFields.findIndex((f) => f.key === over.id);

              if (from !== -1 && to !== -1) {
                const fromFullIndex = fields.findIndex(
                  (f) => f.key === filteredFields[from].key
                );
                const toFullIndex = fields.findIndex(
                  (f) => f.key === filteredFields[to].key
                );

                if (fromFullIndex !== -1 && toFullIndex !== -1) {
                  move(fromFullIndex, toFullIndex);
                }
              }
            };

            const handleAdd = () => {
              const maxPriority = filteredFields.length + 1;

              add({
                text: '',
                id: null,
                dateRange: [null, null],
                priority: maxPriority,
                temporaryId: Date.now(),
                kind,
                packageId: packageId ?? null,
              });
            };

            return (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredFields.map((f) => f.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="scard-container">
                    {filteredFields.map((field) => (
                      <DisclaimerBloc
                        key={field.key}
                        listName="disclaimers"
                        fieldName={field.name}
                        sortableId={field.key}
                        onRemove={() => remove(field.name)}
                      />
                    ))}

                    {isEditMode && (
                      <div className="container-button">
                        <Button size="middle" onClick={handleAdd}>
                          + Add
                        </Button>
                      </div>
                    )}

                    {!isEditMode && !filteredFields.length && <Empty />}
                  </div>
                </SortableContext>
              </DndContext>
            );
          }}
        </Form.List>
      </SCard>
    </div>
  );
}
